import { memo, MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { Reorder, useDragControls } from 'motion/react';
import { shallow } from 'zustand/shallow';
import { DentalInsuranceReportTypes } from '@frontend/api-insurance-eligibility-report';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import {
  Button,
  PopoverDialog,
  usePopoverDialog,
  Text,
  useFormField,
  HamburgerMenuIcon,
  CheckboxField,
  useTooltip,
  Dot,
  CustomChangeEvent,
} from '@frontend/design-system';
import { insuranceReportTrackingIds } from '../../../constants';
import { useInsuranceReportAccordionContext } from '../../../shared/context/AccordionContext';
import {
  getAllInsuranceReportTableColumnKeys,
  getInsuranceReportTableColumns,
  getInsuranceReportTableColumnsListByIds,
  InsuranceReportTableColumnKeysType,
} from '../components/InsuranceTables/insurance-reports-table-columns.helper';
import { useDentalInsuranceReportContext } from '../context/DentalInsuranceReportDetailsContext.provider';
import { TablesDataType, Vendors } from '../types/types';
import {
  useInsuranceReportTableFiltersColumns,
  useInsuranceTableFiltersColumnsShallowStore,
} from './useInsuranceTableFiltersColumnsStore';

type UseInsuranceTableColumnFiltersProps = {
  tableId: keyof TablesDataType;
  locationId: string;
  isPrintReport?: boolean;
};

type InsuranceReportTableColumnType = InsuranceReportTableColumnKeysType & {
  isChecked: boolean;
};

type InsuranceReportTableColumnConfigSimpleType = Omit<InsuranceReportTableColumnKeysType, 'label'>;

const MAX_ALLOWED_COLUMNS = 6;

export const useGetUniqueOrderColumnsId = (tableId: string, locationId: string) => {
  return `insurance-report-${tableId}-columns-order-${locationId}`;
};

// This function is used to retrieve the cached columns settings from session storage
// It checks if the columns are already saved in session storage and returns them
const getCachedInsuranceColumnsSetting = (uniqueOrderColumnsId: string): InsuranceReportTableColumnType[] => {
  try {
    const selectedTableColumnsLocalStorageValue = window.sessionStorage.getItem(uniqueOrderColumnsId);
    if (selectedTableColumnsLocalStorageValue) {
      const parsedColumns = JSON.parse(selectedTableColumnsLocalStorageValue) as InsuranceReportTableColumnType[];
      return parsedColumns;
    }
  } catch (e) {
    console.error(`Error retrieving columns from local storage: `, e);
  }
  return [];
};

// This function is used to save the selected columns in session storage
// so that the user can have their preferred columns displayed when they refresh the page
const persistUserPreference = (id: string, values?: InsuranceReportTableColumnType[]) => {
  try {
    window.sessionStorage.setItem(id, JSON.stringify(values));
  } catch (e) {
    console.error(`Saving this preference was unsuccessful due to an error `, e);
  }
};

/**
 * A helper function to determine and sort the columns for a specific table
 * based on its default configuration and a global list of all available columns.
 */
const getSortedTableColumns = (
  tableId: keyof TablesDataType,
  accordionIds: (keyof DentalInsuranceReportTypes.DentalXChangeDentalSummaryInfo)[],
  vendor: Vendors,
  allAvailableColumns: InsuranceReportTableColumnKeysType[]
): InsuranceReportTableColumnKeysType[] => {
  // Determine the default column order for the current table.
  // This function provides the specific columns and their preferred order for 'tableId'.
  const defaultTableColumnsConfig = getInsuranceReportTableColumns(tableId, accordionIds, vendor);

  // Create a map to quickly look up the desired index (order) of each column.
  // This map acts as a blueprint for sorting.
  const columnOrderMap: Record<string, number> = {};
  defaultTableColumnsConfig.forEach((column, index) => {
    // Ensure the column is a simple object (not an array) and has an ID.
    if (!Array.isArray(column) && column?.id) {
      columnOrderMap[column.id] = index;
    }
  });

  // Sort all available columns based on the default order for the current table.
  // Columns specified in 'defaultTableColumnsConfig' will appear first in their defined order.
  // Any other columns (not in 'defaultTableColumnsConfig') will appear at the end.
  const sortedAllColumnsForTable = [...allAvailableColumns].sort((a, b) => {
    // Get the desired index for column 'a'. If not found, assign Infinity to push it to the end.
    const indexA = columnOrderMap[a.id] ?? Infinity;
    const indexB = columnOrderMap[b.id] ?? Infinity;
    return indexA - indexB;
  });

  return sortedAllColumnsForTable;
};

/**
 * Generates the default column configuration for a given table.
 * It marks columns as checked if they are part of the defaultTableConfig,
 * otherwise, they are marked as unchecked.
 */
export const generateDefaultTableColumns = (
  tableId: keyof TablesDataType,
  vendor: Vendors,
  accordionIds: (keyof DentalInsuranceReportTypes.DentalXChangeDentalSummaryInfo)[], // Passed if needed by getInsuranceReportTableColumns
  sortedColumns: InsuranceReportTableColumnKeysType[]
): InsuranceReportTableColumnType[] => {
  const allColumnsConfig = sortedColumns ? sortedColumns : getAllInsuranceReportTableColumnKeys(vendor);
  const defaultTableConfig = getInsuranceReportTableColumns(tableId, accordionIds, vendor);

  const defaultColumns: InsuranceReportTableColumnType[] = [];
  allColumnsConfig.forEach((column) => {
    if (column?.id) {
      const columnDetails = (defaultTableConfig as InsuranceReportTableColumnConfigSimpleType[]).find(
        (col) => col.id === (column.id as string)
      );
      if (columnDetails) {
        defaultColumns.push({ ...column, isChecked: true });
      } else {
        defaultColumns.push({
          id: column.id,
          label: column.label,
          isChecked: false,
        });
      }
    }
  });

  return defaultColumns.length ? defaultColumns : [];
};

// This function is responsible for initializing all table column settings
export const initializeAllTableColumnFilters = (
  setColumnsFilters: (tableId: keyof TablesDataType, columns: InsuranceReportTableColumnType[]) => void,
  vendor: Vendors,
  accordionIds: (keyof DentalInsuranceReportTypes.DentalXChangeDentalSummaryInfo)[],
  locationId: string,
  tablesDataKeys: (keyof TablesDataType)[]
) => {
  const allColumnsConfig = getAllInsuranceReportTableColumnKeys(vendor);

  tablesDataKeys.forEach((tableId) => {
    const uniqueOrderColumnsId = `insurance-report-${tableId}-columns-order-${locationId}`;
    const cachedColumnsSettings = getCachedInsuranceColumnsSetting(uniqueOrderColumnsId);

    if (cachedColumnsSettings?.length) {
      setColumnsFilters(tableId, cachedColumnsSettings);
      return; // If cached, moving to next table
    }
    const sortedColumnsForTable = getSortedTableColumns(tableId, accordionIds, vendor, allColumnsConfig);
    const defaultColumns = generateDefaultTableColumns(tableId, vendor, accordionIds, sortedColumnsForTable);
    // Set the default columns for this table in the store
    setColumnsFilters(tableId, defaultColumns?.length ? defaultColumns : []);
  });
};

export const useInsuranceTableColumnFilters = ({
  tableId,
  isPrintReport,
  locationId,
}: UseInsuranceTableColumnFiltersProps) => {
  const { setColumnsFilters } = useInsuranceTableFiltersColumnsShallowStore('setColumnsFilters');
  const columnListData = useInsuranceReportTableFiltersColumns((state) => state.columnsFilters[tableId], shallow);
  const { vendor } = useDentalInsuranceReportContext();
  const { accordionIds } = useInsuranceReportAccordionContext();
  const uniqueOrderColumnsId = useGetUniqueOrderColumnsId(tableId, locationId);

  // useMemo is used to memoize the column list by sorting all the active columns on the top of the filter list
  const { allColumnsConfig, defaultTableConfig } = useMemo(() => {
    const allColumnsConfig = getAllInsuranceReportTableColumnKeys(vendor);
    const defaultTableConfig = getInsuranceReportTableColumns(tableId, accordionIds, vendor);
    const sortedColumns = getSortedTableColumns(tableId, accordionIds, vendor as Vendors, allColumnsConfig);
    return {
      allColumnsConfig: sortedColumns,
      defaultTableConfig,
    };
  }, [tableId]);

  // Set the default columns based on the session storage values or defaultConfig values If it is not set in global level
  // This will configure the columns for the table on the first render
  useEffect(() => {
    if (!columnListData?.length) {
      // If cached columns settings are available, set them as selected columns
      const cachedColumnsSettings = getCachedInsuranceColumnsSetting(uniqueOrderColumnsId);
      if (!!cachedColumnsSettings?.length) {
        setColumnsFilters(tableId, cachedColumnsSettings);
      }
      const defaultColumns = generateDefaultTableColumns(tableId, vendor!, accordionIds, allColumnsConfig);
      setColumnsFilters(tableId, defaultColumns?.length ? defaultColumns : []);
    }
  }, [tableId, allColumnsConfig, defaultTableConfig, uniqueOrderColumnsId]);

  // useMemo is used to memoize the table columns list
  // If the cached columns settings are available, it will use them
  // Otherwise, it will use the columnListData from the store
  const tableColumnsList = useMemo(() => {
    const cachedColumnsSettings = getCachedInsuranceColumnsSetting(uniqueOrderColumnsId);
    if (!!cachedColumnsSettings?.length) {
      return cachedColumnsSettings;
    }
    if (!!columnListData?.length) {
      return columnListData;
    }

    return [];
  }, [columnListData, uniqueOrderColumnsId]);

  // Check if the filter is applied by comparing the tableColumnsList with the default columns
  const isFilterApplied = useMemo<boolean>(() => {
    if (isPrintReport) {
      return false;
    }

    const defaultIdsWithIndices = (defaultTableConfig as InsuranceReportTableColumnConfigSimpleType[]).map(
      (item, index) => ({ id: item.id, index })
    );

    const defaultIdIndexSet = new Set(defaultIdsWithIndices.map((item) => `${item.id}-${item.index}`));

    const columnsFiltersList = getCachedInsuranceColumnsSetting(uniqueOrderColumnsId);
    const selectedColumnsList = columnsFiltersList?.length ? columnsFiltersList : tableColumnsList || [];

    const isDifferent = !!selectedColumnsList?.some((col, index) => {
      return col.isChecked && !defaultIdIndexSet.has(`${col.id}-${index}`);
    });

    return isDifferent;
  }, [defaultTableConfig, isPrintReport, tableColumnsList]);

  // This function is used to set the columns filters
  const setColumnsListFilters = useCallback(
    (columnsFilters: InsuranceReportTableColumnType[]) => {
      if (columnsFilters) {
        setColumnsFilters(tableId, columnsFilters);
      }
    },
    [tableId]
  );

  return {
    tableColumnsList: tableColumnsList?.length
      ? getInsuranceReportTableColumnsListByIds(tableColumnsList, vendor as Vendors)
      : [],
    InsuranceReportTableColumnFilter: !isPrintReport ? (
      <InsuranceReportTableColumnFilter
        tableId={tableId}
        locationId={locationId}
        columnsFilters={tableColumnsList || []}
        setColumnsFilters={setColumnsListFilters}
        isPrintReport={isPrintReport || false}
        isFilterApplied={isFilterApplied}
      />
    ) : null,
  };
};

type InsuranceReportTableColumnFilterProps = {
  tableId: keyof DentalInsuranceReportTypes.DentalXChangeDentalSummaryInfo;
  locationId: string;
  columnsFilters: InsuranceReportTableColumnType[];
  setColumnsFilters: (columnsFilters: InsuranceReportTableColumnType[]) => void;
  isPrintReport: boolean;
  isFilterApplied?: boolean;
};

const InsuranceReportTableColumnFilter = memo(
  ({
    tableId,
    locationId,
    columnsFilters,
    setColumnsFilters,
    isPrintReport,
    isFilterApplied,
  }: InsuranceReportTableColumnFilterProps) => {
    const { t } = useTranslation('insuranceEligibilityReport');
    const constraintRef = useRef<HTMLUListElement | null>(null);

    const [columnList, setColumnList] = useState(columnsFilters);
    const [isDragging, setIsDragging] = useState(false);

    const uniqueOrderColumnsId = useGetUniqueOrderColumnsId(tableId, locationId);

    const { getDialogProps, getTriggerProps } = usePopoverDialog<HTMLButtonElement>();
    const { tooltipProps, triggerProps, Tooltip } = useTooltip({
      placement: 'right',
    });

    const shouldDisableCheckbox = columnsFilters?.filter((col) => col.isChecked).length >= MAX_ALLOWED_COLUMNS;

    useEffect(() => {
      if (!columnList.length) {
        setColumnList(columnsFilters);
      }
    }, [columnsFilters]);

    const onReorder = (newOrder: InsuranceReportTableColumnType[]) => {
      setColumnList(newOrder);
    };

    const onDragStart = () => setIsDragging(true);

    const onCheckboxChange = (e: CustomChangeEvent, index: number) => {
      if (!columnList) return;

      const updatedColumnList = [...columnList];
      const column = updatedColumnList[index];
      const isChecked = e.value;
      const updatedColumn = { ...column, isChecked };
      updatedColumnList[index] = updatedColumn;
      setColumnList(updatedColumnList);
      persistUserPreference(uniqueOrderColumnsId, updatedColumnList);
      setColumnsFilters(updatedColumnList);
    };

    const onDragEnd = useCallback(() => {
      if (columnList) {
        setColumnsFilters(columnList);
        persistUserPreference(uniqueOrderColumnsId, columnList);
      }
      setIsDragging(false);
    }, [columnList]);

    return (
      <>
        <section {...triggerProps}>
          {isFilterApplied && <Dot color='primary' css={dotStyles} />}
          <Button
            trackingId={insuranceReportTrackingIds.tableColumnsFilterBtn}
            variant='secondary'
            iconName='columns'
            color='default'
            {...getTriggerProps()}
          />
        </section>
        <Tooltip {...tooltipProps}>{t('Customize your table by filtering columns')}</Tooltip>
        <PopoverDialog
          css={{ borderRadius: theme.borderRadius.small, padding: 0, height: theme.spacing(50) }}
          key={tableId}
          {...getDialogProps()}
        >
          <div css={{ padding: theme.spacing(2), borderBottom: `1px solid ${theme.colors.neutral10}` }}>
            <Text weight='bold' color='subdued'>
              {t('Filter Columns')}
            </Text>
          </div>

          <Reorder.Group
            transition={{
              layout: {
                duration: 0,
              },
            }}
            initial={false}
            layout='position'
            css={{
              position: 'relative',
              padding: 0,
            }}
            axis='y'
            ref={constraintRef}
            values={columnList}
            onReorder={onReorder}
          >
            {columnList.map((column, index) => (
              <InsuranceReportTableColumnFilterItem
                key={tableId + column.id + isPrintReport + index}
                column={column}
                constraintRef={constraintRef}
                onDragEnd={onDragEnd}
                onDragStart={onDragStart}
                isDragging={isDragging}
                shouldDisableCheckbox={shouldDisableCheckbox}
                index={index}
                onCheckboxChange={onCheckboxChange}
              />
            ))}
          </Reorder.Group>
        </PopoverDialog>
      </>
    );
  }
);

const dotStyles = css({
  position: 'relative',
  width: theme.spacing(1.5),
  height: theme.spacing(1.5),
  top: theme.spacing(1.5),
  left: theme.spacing(2.5),
  zIndex: theme.zIndex.high,
});

type InsuranceReportTableColumnFilterItemProps = {
  index: number;
  column: InsuranceReportTableColumnType;
  constraintRef: MutableRefObject<HTMLUListElement | null>;
  isDragging: boolean;
  shouldDisableCheckbox: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onCheckboxChange: (e: CustomChangeEvent, index: number) => void;
};
const InsuranceReportTableColumnFilterItem = memo(
  ({
    index,
    column,
    constraintRef,
    isDragging,
    shouldDisableCheckbox,
    onDragStart,
    onDragEnd,
    onCheckboxChange,
  }: InsuranceReportTableColumnFilterItemProps) => {
    const { t } = useTranslation('insuranceEligibilityReport');

    const columnFilterCheckboxFieldProps = useFormField({
      type: 'checkbox',
    });

    const { tooltipProps, triggerProps, Tooltip } = useTooltip({
      placement: 'right',
    });

    const dragControls = useDragControls();

    const isChecked = column?.isChecked ?? false;
    const isDisabled = shouldDisableCheckbox && !isChecked;

    return (
      <>
        <Reorder.Item
          {...triggerProps}
          initial={false}
          key={column.id + column.label + column.isChecked}
          id={column.id}
          value={column}
          dragListener={false}
          dragElastic={0.05}
          as={'ul'}
          dragConstraints={constraintRef}
          dragControls={dragControls}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          drag={!isDisabled}
          style={{
            position: 'relative',
            minHeight: theme.spacing(5),
            listStyle: 'none',
            display: 'flex',
            alignItems: 'center',
            padding: theme.spacing(1, 2),
            background: theme.colors.white,
            cursor: isDisabled ? 'not-allowed' : isDragging ? 'grabbing' : 'grab',
          }}
        >
          <HamburgerMenuIcon
            size={20}
            color='light'
            onPointerDown={(event) => dragControls.start(event)}
            css={{
              marginRight: theme.spacing(1.5),
              cursor: isDisabled ? 'not-allowed' : isDragging ? 'grabbing' : 'grab',
              color: isDisabled ? theme.colors.neutral10 : theme.colors.neutral50,
            }}
          />
          <CheckboxField
            {...columnFilterCheckboxFieldProps}
            name={column.id}
            label={column.label}
            labelPlacement='left'
            value={isChecked}
            disabled={isDisabled}
            onChange={(event) => event && onCheckboxChange(event as CustomChangeEvent, index)}
            css={{
              marginRight: theme.spacing(1.5),
              cursor: isDragging ? 'grabbing' : 'grab',
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          />
        </Reorder.Item>

        {isDisabled && (
          <Tooltip {...tooltipProps}>
            {t('You’ve exceeded the maximum limit of {{selectionNumber}} selections.', {
              selectionNumber: MAX_ALLOWED_COLUMNS,
            })}
          </Tooltip>
        )}
      </>
    );
  }
);

InsuranceReportTableColumnFilter.displayName = 'InsuranceReportTableColumnFilterProps';
InsuranceReportTableColumnFilterItem.displayName = 'InsuranceReportTableColumnFilterItem';
